import React, { useState, useEffect } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  Typography,
  FormControl,
  InputLabel,
  TextField,
  InputAdornment,
  FormControlLabel,
  Switch,
  IconButton,
  useTheme,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckIcon from "@mui/icons-material/Check";

const formatCurrency = (value) => {
  if (!value) return "";
  return Number(value).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
};

const parseCurrency = (value) => {
  if (!value) return "";
  return Number(
    value
      .replace(/\s/g, "")
      .replace("R$", "")
      .replace(/\./g, "")
      .replace(",", ".")
  );
};

const EditDiscountModal = ({ open, onClose, user, onConfirm }) => {
  const theme = useTheme();

  const [selectedDiscount, setSelectedDiscount] = useState("none");
  const [customValue, setCustomValue] = useState("");
  const [isTestOffer, setIsTestOffer] = useState(false);
  const [subscriptionDays, setSubscriptionDays] = useState("30");
  const [isEditingDays, setIsEditingDays] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setSelectedDiscount(user.discount_type || "none");
    setCustomValue(user.custom_value ? formatCurrency(user.custom_value) : "");
    const hasCustomDays = Number(user.subscription_days_override) > 0;
    setIsTestOffer(hasCustomDays);
    setSubscriptionDays(
      hasCustomDays ? String(user.subscription_days_override) : "30"
    );
    setIsEditingDays(false);
  }, [user]);

  const handleConfirm = async () => {
    if (isSubmitting) return;

    let parsedValue = null;

    if (selectedDiscount === "VALOR_LIVRE") {
      parsedValue = parseCurrency(customValue);

      if (!Number.isFinite(parsedValue) || parsedValue < 5) {
        alert("Informe um valor de pelo menos R$ 5,00 para gerar o Pix.");
        return;
      }
    }

    const parsedDays = Number(subscriptionDays);
    if (
      isTestOffer &&
      (!Number.isInteger(parsedDays) || parsedDays <= 0 || parsedDays > 365)
    ) {
      alert("Informe uma quantidade de dias válida entre 1 e 365.");
      return;
    }

    setIsSubmitting(true);
    try {
      await onConfirm({
        discount_type: selectedDiscount,
        custom_value: selectedDiscount === "VALOR_LIVRE" ? parsedValue : null,
        subscription_days_override: isTestOffer ? parsedDays : null,
      });
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          fontWeight: "bold",
        }}
      >
        Editar Desconto
      </DialogTitle>

      <DialogContent sx={{ padding: theme.spacing(3) }}>
        <Typography sx={{ mb: 2 }}>
          Desconto Atual:{" "}
          <strong style={{ color: theme.palette.primary.main }}>
            {user.discount_type || "Nenhum"}
          </strong>
        </Typography>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="discount-label">Selecionar Desconto</InputLabel>
          <Select
            labelId="discount-label"
            value={selectedDiscount}
            label="Selecionar Desconto"
            onChange={(e) => {
              const nextDiscount = e.target.value;
              setSelectedDiscount(nextDiscount);
              if (nextDiscount !== "VALOR_LIVRE") {
                setCustomValue("");
                setIsTestOffer(false);
                setSubscriptionDays("30");
                setIsEditingDays(false);
              }
              if (nextDiscount === "none") {
                setIsTestOffer(false);
                setSubscriptionDays("30");
                setIsEditingDays(false);
              }
            }}
          >
            <MenuItem value="none">Nenhum</MenuItem>
            <MenuItem value="Desconto 199">Desconto 199</MenuItem>
            <MenuItem value="Desconto 179">Desconto 179</MenuItem>
            <MenuItem value="Desconto 149">Desconto 149</MenuItem>
            <MenuItem value="VALOR_LIVRE">Valor personalizado</MenuItem>
          </Select>
        </FormControl>

        {selectedDiscount === "VALOR_LIVRE" && (
          <TextField
            fullWidth
            label="Valor personalizado"
            placeholder="R$ 0,00"
            value={customValue}
            onChange={(e) => {
              const raw = e.target.value.replace(/\D/g, "");
              const numeric = Number(raw) / 100;
              setCustomValue(formatCurrency(numeric));
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">💰</InputAdornment>
              ),
            }}
          />
        )}

        {selectedDiscount === "VALOR_LIVRE" && (
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isTestOffer}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    setIsTestOffer(enabled);
                    setSubscriptionDays((current) =>
                      enabled ? current || "30" : "30"
                    );
                    setIsEditingDays(false);
                  }}
                />
              }
              label="Marcar como teste"
            />

            {isTestOffer && (
              <TextField
                fullWidth
                type="number"
                label="Duração do acesso"
                value={subscriptionDays}
                onChange={(e) => setSubscriptionDays(e.target.value)}
                inputProps={{ min: 1, max: 365 }}
                helperText="Vem com 30 dias por padrão, mas você pode ajustar quando precisar."
                InputProps={{
                  readOnly: !isEditingDays,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography
                        sx={{ color: theme.palette.text.secondary, mr: 0.5 }}
                      >
                        dias
                      </Typography>
                      <IconButton
                        edge="end"
                        onClick={() => setIsEditingDays((prev) => !prev)}
                        size="small"
                      >
                        {isEditingDays ? (
                          <CheckIcon fontSize="small" />
                        ) : (
                          <EditOutlinedIcon fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mt: 1 }}
              />
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
        <Button onClick={onClose} variant="outlined" color="error">
          Cancelar
        </Button>

        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Salvando..." : "Confirmar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditDiscountModal;
